<template>
  <div>
    <div class="w-100 d-inline-block pets-banner-slider position-relative py-3">
      <!-- Floating side pets (Left) -->
      <div class="side-pets side-pets-left d-none d-xl-flex">
        <img src="@/assets/images/service-cat-1.png" class="floating-left-cat" alt="cat" />
      </div>
      
      <!-- Floating side pets (Right) -->
      <div class="side-pets side-pets-right d-none d-xl-flex">
        <img src="@/assets/images/service-dog-1.png" class="floating-right-dog" alt="dog" />
      </div>

      <div class="container autoContent position-relative z-1 text-center mt-xl-4 mt-2">
        <!-- Top Text Section -->
        <p class="mb-2 text-size-14 nav-color family-filson-medium fw-bold">
          First-Class Grooming For Your Furry Friend
        </p>
        <h1 class="text-black fw-bold filson-black-family mb-3" style="font-size: 40px;">
          {{ mainHeading }}
        </h1>
        <p class="mx-auto text-size-14 text-muted family-inter-light mb-4 text-lh-large" style="max-width: 800px;">
          {{ mainContent }}
        </p>
        
        <router-link class="btn text-white px-5 py-2 fw-bold text-size-14 family-filson-bold mt-2 d-inline-block" style="background-color: #F8721F;" to="/booking/">
          BOOK NOW
        </router-link>

        <!-- Services Row -->
        <div class="row pt-5 mt-xl-4 mt-2 justify-content-center">
          <div
            v-for="(cat, idx) in serviceCategories"
            :key="cat.id || idx"
            class="col-xl-3 col-lg-3 col-md-6 mb-4 d-flex"
          >
            <div class="w-100 d-inline-block d-flex align-items-center text-start">
              <div class="me-3">
                <img
                  v-lazy="categoryImage(cat, idx)"
                  class="img-fluid rounded-circle service-category-icon"
                  alt="service"
                >
              </div>
              <div>
                <p class="mb-1 text-size-14 fw-bold text-black family-filson-bold text-uppercase">{{ cat.name }}</p>
                <p class="mb-2 text-size-12 text-muted family-inter-light">
                  {{ cat.description || defaultServiceDesc }}
                </p>
                <router-link
                  class="fw-bold text-size-12 nav-color inter-bold-family d-flex align-items-center"
                  :to="categoryLink(cat)"
                >
                  Book Now <i class="fa fa-arrow-right ms-2" style="font-size: 10px;"></i>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { withCountryPath } from '@/utils/countryPath';

export default {
  name: 'serviceBannerComponent',
  data() {
    return {
      mainHeading: 'Lush Groom',
      mainContent:
        "At Lush Groom, we see grooming as more than just making pets look good — it's about their overall well-being. As fellow animal lovers, we get that your dog is a part of your family. That's why we're committed to offering stress-free, flexible, and top-quality grooming services that meet your needs. Our certified groomers are skilled in working with all breeds and personalities, providing personalized care for each dog.",
      serviceCategories: [],
      defaultServiceDesc: 'All it takes is a little trimming and bath for a picture-perfect pet.',
      iconPool: [
        require('@/assets/images/cute-dog-icon.webp'),
        require('@/assets/images/cute-cat-icon.png'),
        require('@/assets/images/cute-rabbit-icon.webp'),
      ],
    };
  },
  methods: {
    async fetchServicePageContent() {
      try {
        const res = await this.$axios.post('/get-service-page', {
          website_name: this.activeWebsiteName,
        });
        const page = res?.data?.service_page;
        if (!page) return;
        this.mainHeading = page.main_heading || this.mainHeading;
        this.mainContent = page.main_content || this.mainContent;
      } catch (e) {
        // keep defaults to avoid UI changes
        console.error('fetchServicePageContent', e);
      }
    },
    categoryImage(cat, idx) {
      if (cat?.image) return this.$getImageUrl(cat.image);
      return this.categoryIconByIndex(idx);
    },
    categoryIconByIndex(idx) {
      return this.iconPool[idx % this.iconPool.length];
    },
    categoryLink(cat) {
      const rawUrl = typeof cat?.content?.service_url === 'string' ? cat.content.service_url.trim() : '';
      if (rawUrl) {
        if (/^https?:\/\//i.test(rawUrl)) return rawUrl;
        const path = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
        return withCountryPath(path);
      }
      if (cat?.slug) return withCountryPath(`/services/${cat.slug}/`);
      return withCountryPath('/services/');
    },
    async fetchServiceCategories() {
      try {
        const form = new URLSearchParams();
        form.append('website_name', this.activeWebsiteName || '');
        const res = await this.$axios.post('/get-categories-home', form);
        if (res?.data?.status === 'success' && Array.isArray(res.data.categories)) {
          this.serviceCategories = res.data.categories
            .filter((c) => c && String(c.status) !== '0')
            .slice(0, 4);
        }
      } catch (e) {
        // keep existing UI; just fall back to empty (row won't render items)
        console.error('fetchServiceCategories', e);
      }
    },
  },
  mounted() {
    this.fetchServicePageContent();
    this.fetchServiceCategories();
  },
  watch: {
    activeWebsiteName() {
      this.fetchServicePageContent();
      this.fetchServiceCategories();
    },
  },
}
</script>

<style scoped>
.text-lh-large {
  line-height: 1.8;
}

/* Floating Pets styling */
.side-pets {
  position: absolute;
  bottom: -20px;
  z-index: 0;
  align-items: flex-end;
}
.side-pets-left {
  left: 0;
}
.side-pets-right {
  right: 0;
}
.floating-left-cat {
  height: 250px;
  object-fit: contain;
  margin-right: -20px;
  z-index: 2;
}
.floating-left-rabbit {
  height: 150px;
  object-fit: contain;
  margin-bottom: 10px;
}
.floating-right-dog {
  height: 300px;
  object-fit: contain;
}

.service-category-icon {
  width: 110px;
  height: 110px;
  object-fit: contain;
}

/* 14" / mid-desktop widths: slightly larger icons */
@media (min-width: 1200px) and (max-width: 1399.98px) {
  .service-category-icon {
    width: 130px;
    height: 130px;
  }
}

@media (max-width: 991.98px) {
  .service-category-icon {
    width: 90px;
    height: 90px;
  }
}

@media (max-width: 991.98px) {
  .service-banner-wrapper {
    padding-top: 2rem !important;
    padding-bottom: 2rem !important;
  }
}
</style>
